/**
 * Bulk operations (FS-030).
 *
 * One endpoint, one selection shape, one result shape — replacing four copies
 * of a loop that fired one request per row and threw the failures away.
 *
 * The selection is the interesting part. A list of ids cannot say "all 593
 * machines matching this filter" when the table only rendered 25 of them, so a
 * selection can instead be the filter itself. That shifts the resolution to the
 * server, which means it happens *later* than the user made it — so a filter
 * selection carries `as_of` (when this client read the list) and
 * `expected_count` (what it showed). The server excludes rows created after
 * `as_of`, and refuses outright if the count no longer matches.
 */

import { useMutation, useQuery } from "@tanstack/react-query";
import { apiClient } from "@/api/client";
import type { FilterNode } from "@/features/inventory/filterTree";

/** What the user picked. Either `ids`, or a filter plus its pinning fields. */
export interface BulkSelection {
  ids?: string[];
  /** FS-029's tree, passed straight through so the server resolves it with
   *  the same interpreter that produced the list on screen. */
  filters?: FilterNode | null;
  search?: string | null;
  statuses?: string[];
  location_id?: string | null;
  show_archived?: boolean;
  /** Rows unticked after "select all". The reason a filter beats a list. */
  excluded_ids?: string[];
  as_of?: string;
  expected_count?: number;
}

export interface BulkOperationInfo {
  key: string;
  label: string;
  entity: string;
  params: string[];
  confirm: string | null;
  destructive: boolean;
  supports_query_selection: boolean;
}

export interface BulkRowOutcome {
  id: string;
  label: string;
  ok: boolean;
  error: string | null;
}

export interface BulkOutcome {
  operation: string;
  requested: number;
  succeeded: number;
  failed: number;
  /** Failures first — the server sorts them, so the panel does not have to. */
  rows: BulkRowOutcome[];
  message: string;
}

/**
 * The operations this user may run, already filtered by permission.
 *
 * The labels and the confirmation sentences come with them, so a button and
 * the thing it does are described in one place rather than two.
 */
export function useBulkOperations(entity?: string) {
  return useQuery({
    queryKey: ["bulk-operations"],
    queryFn: async () => {
      const { data } = await apiClient.get<{
        items: BulkOperationInfo[];
        max_rows: number;
      }>("/api/v1/bulk/operations");
      return data;
    },
    staleTime: 5 * 60 * 1000,
    select: (data) =>
      entity
        ? {
            ...data,
            items: data.items.filter(
              (op: BulkOperationInfo) => op.entity === entity,
            ),
          }
        : data,
  });
}

/** How many rows a filter selection names, for the "Select all N" affordance. */
export function useBulkCount(selection: BulkSelection, enabled: boolean) {
  return useQuery({
    queryKey: ["bulk-count", selection],
    queryFn: async () => {
      const { data } = await apiClient.post<{
        count: number;
        max_rows: number;
        over_limit: boolean;
      }>("/api/v1/bulk/count", selection);
      return data;
    },
    enabled,
  });
}

export function useBulkRun() {
  return useMutation({
    mutationFn: async (input: {
      operation: string;
      selection: BulkSelection;
      params?: Record<string, unknown>;
    }) => {
      const { data } = await apiClient.post<BulkOutcome>("/api/v1/bulk", {
        operation: input.operation,
        selection: input.selection,
        params: input.params ?? {},
      });
      return data;
    },
  });
}

/**
 * Selection state for a list page.
 *
 * Two modes. `ids` is "they ticked these"; `all` is "everything matching the
 * current filter, less what they unticked". `asOf` is stamped when the mode
 * becomes `all`, which is the moment the count on screen was true.
 */
export interface SelectionState {
  mode: "ids" | "all";
  ids: Set<string>;
  excluded: Set<string>;
  asOf: string | null;
  /** What the list showed when `all` was chosen. */
  total: number;
}

export const EMPTY_SELECTION: SelectionState = {
  mode: "ids",
  ids: new Set(),
  excluded: new Set(),
  asOf: null,
  total: 0,
};

/** How many rows the selection stands for, for the action bar's count. */
export function selectionSize(state: SelectionState): number {
  return state.mode === "all"
    ? Math.max(state.total - state.excluded.size, 0)
    : state.ids.size;
}

export function isRowSelected(state: SelectionState, id: string): boolean {
  return state.mode === "all" ? !state.excluded.has(id) : state.ids.has(id);
}

/**
 * Tick or untick one row.
 *
 * In `all` mode unticking adds an exclusion rather than dropping an id, because
 * there is no list of ids to drop from — that is the whole point of the mode.
 */
export function toggleRow(state: SelectionState, id: string): SelectionState {
  if (state.mode === "all") {
    const excluded = new Set(state.excluded);
    if (excluded.has(id)) excluded.delete(id);
    else excluded.add(id);
    return { ...state, excluded };
  }
  const ids = new Set(state.ids);
  if (ids.has(id)) ids.delete(id);
  else ids.add(id);
  return { ...state, ids };
}

export function selectAllMatching(total: number): SelectionState {
  return {
    mode: "all",
    ids: new Set(),
    excluded: new Set(),
    // Stamped here: this is the moment the count was true.
    asOf: new Date().toISOString(),
    total,
  };
}

/**
 * The selection as the server wants it.
 *
 * `query` is the page's current filter, passed straight through so the server
 * resolves it with the same interpreter that produced the list on screen.
 */
export function toBulkSelection(
  state: SelectionState,
  query: Omit<
    BulkSelection,
    "ids" | "excluded_ids" | "as_of" | "expected_count"
  >,
): BulkSelection {
  if (state.mode === "ids") {
    return { ids: [...state.ids] };
  }
  return {
    ...query,
    excluded_ids: [...state.excluded],
    as_of: state.asOf ?? new Date().toISOString(),
    expected_count: state.total,
  };
}
